import { FC, useEffect } from 'react';
import { If, Loader, SidePanel } from 'components';
import { CrossIconvector } from 'assets/icons';
import { PayRunMemberJob } from '../confirmJob.model';
import { useGetJobCommissionExplainQuery } from '../api';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { CommissionType } from 'containers/settings/commission-settings/constants';
import { getAmountWithCurrency, roundToTwoDecimals } from 'utils/amountUtils';
import { PayRunsCleverTapEvents } from '../../events';
import { logAnalyticEvent } from 'utils/analytics';
import { getCommissionBaseLabel } from 'containers/settings/utils';

interface ExplainCommissionPanelProps {
  isOpen: boolean;
  onClose: () => void;
  jobData: PayRunMemberJob | { jobId: string; title: string; jobNumber: string };
  merchantId: string;
}

const ExplainCommissionPanel: FC<ExplainCommissionPanelProps> = ({ isOpen, onClose, jobData, merchantId }) => {
  const team = useAppSelector(selectCurrentTeam);
  const { data, isFetching } = useGetJobCommissionExplainQuery({
    teamId: team.id.toString(),
    jobId: jobData.jobId,
    merchantId: merchantId as string
  });

  useEffect(() => {
    logAnalyticEvent(PayRunsCleverTapEvents.webPayRunExplainJobCommissionView);
  }, [jobData.jobId]);

  const commissionData = data?.data;

  const getRateDisplay = () => {
    const commissionAmt = commissionData?.commissionAmount
      ? getAmountWithCurrency(commissionData?.commissionAmount)
      : '-';
    if (commissionData?.commissionExecutionType === 'MANUAL') return commissionAmt;

    if (!commissionData?.commissionType) return commissionData?.commissionRate;
    if (commissionData.commissionType === CommissionType.ABSOLUTE) return '-';
    if (commissionData.commissionType === CommissionType.PERCENTAGE)
      return `${roundToTwoDecimals(commissionData?.commissionRate)}%`;
    return commissionAmt;
  };

  return (
    <SidePanel isOpen={isOpen} onClose={onClose} shouldAnimate={true}>
      <div className="h-full w-[375px]">
        <div className="flex items-center gap-2 px-2.5 pt-5 pb-4">
          <div onClick={onClose} className="cursor-pointer">
            <CrossIconvector id="commission-explain-panel-close" />
          </div>
          <div className="text-17px font-semibold text-headingGray">Commission calculation</div>
        </div>

        <div className="py-6">
          <div className="flex flex-col items-center gap-2 pb-6">
            <div className="text-center text-xl font-bold text-headingGray">{jobData.title}</div>
            <div className="text-sbase font-semibold text-accentText">{jobData.jobNumber}</div>
          </div>
          {isFetching ? (
            <div className="flex h-full items-center justify-center">
              <Loader />
            </div>
          ) : (
            <div className="text-sbase font-semibold text-primaryText">
              <div className="mb-4 px-4">
                {commissionData?.jobLineItems?.map(item => (
                  <div className="flex flex-col gap-2 border-b border-secondaryBtn pb-4" key={item.id}>
                    <div className="flex justify-between">
                      <div>{item.name}</div>
                      <div>{getAmountWithCurrency(item.amount)}</div>
                    </div>
                    <div className="text-px13 text-accentText">
                      ({item.qty} x {getAmountWithCurrency(item.unitPrice)})
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col">
                <If condition={!!commissionData.discountAmount}>
                  <div className="flex justify-between px-4 pb-4">
                    <div>Discount</div>
                    <div className="flex gap-4">
                      <div className="text-accentText">
                        {commissionData.discountType === 'PERCENTAGE' ? '' : '$'}
                        {commissionData.discountValue}
                        {commissionData.discountType === 'PERCENTAGE' ? '%' : ''}
                      </div>
                      {commissionData.discountAmount ? '-' : ''} {getAmountWithCurrency(commissionData?.discountAmount)}
                    </div>
                  </div>
                </If>
                <div className="flex justify-between bg-highlightGray py-3 px-4">
                  <div>Subtotal</div>
                  <div>{getAmountWithCurrency(commissionData?.subTotal)}</div>
                </div>
                <div className="flex flex-col gap-4 p-4">
                  <div className="flex justify-between">
                    <div>Tax</div>
                    <div>{getAmountWithCurrency(commissionData?.taxAmount)}</div>
                  </div>
                  <div className="flex justify-between">
                    <div>Service fee</div>
                    <div>{getAmountWithCurrency(commissionData?.serviceFeeAmount)}</div>
                  </div>
                  <div className="flex justify-between">
                    <div>Service charge</div>
                    <div>{getAmountWithCurrency(commissionData?.serviceChargeAmount)}</div>
                  </div>
                </div>
                <div className="flex justify-between bg-highlightGray py-3 px-4">
                  <div>Total</div>
                  <div>{getAmountWithCurrency(commissionData?.totalAmount)}</div>
                </div>

                <div className="flex justify-between p-4">
                  <div>Rate</div>
                  <div className="text-right">
                    {getRateDisplay()}
                    {commissionData?.commissionExecutionType === 'AUTO' &&
                      commissionData?.commissionType !== CommissionType.ABSOLUTE && (
                        <div className="text-px13 text-accentText">
                          ({roundToTwoDecimals(commissionData?.commissionSplit)}% split)
                        </div>
                      )}
                    {commissionData?.commissionExecutionType === 'MANUAL' && (
                      <div
                        className="flex w-fit items-center rounded-md border border-gray-1 
                      px-1 font-medium text-accentText">
                        Manual
                      </div>
                    )}
                  </div>
                </div>
                <div className="mb-4 px-4">
                  <div className="flex justify-between border-b border-borderGray pb-4">
                    <div>Commission on</div>
                    <div className="text-right">
                      {commissionData?.commissionBaseAmount
                        ? getAmountWithCurrency(commissionData?.commissionBaseAmount)
                        : '-'}
                      {commissionData.commissionBase && (
                        <div className="text-px13 text-accentText">
                          ({getCommissionBaseLabel(commissionData.commissionBase)})
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex justify-between px-4">
                  <div className="text-17px font-bold text-headingGray">Commission</div>
                  <div className="text-right text-17px font-bold text-headingGray">
                    {commissionData?.commissionAmount ? getAmountWithCurrency(commissionData?.commissionAmount) : '-'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </SidePanel>
  );
};

export default ExplainCommissionPanel;
